import { useEffect } from "react";

import { POST_EVENT } from "@/config/postEvent";
import { COLORS, typography } from "@/styles/may-theme";

const BODY_CLASS = "has-post-event-notice";

interface PostEventNoticeProps {
  /**
   * "bar" renders the fixed site-wide notice bar (mounted once by MayHeader).
   * "inline" renders an in-flow block for pages that must show the notice
   * inside their own content (e.g. /tickets, /stay, /go, /gather).
   */
  variant?: "bar" | "inline";
  className?: string;
}

/**
 * Post-event notice. The 2026 event is over; this must be visible in rendered
 * page content on every preserved public page.
 */
export function PostEventNotice({ variant = "bar", className = "" }: PostEventNoticeProps) {
  const isBar = variant === "bar";

  useEffect(() => {
    if (!isBar || typeof document === "undefined") return;
    document.body.classList.add(BODY_CLASS);
    return () => document.body.classList.remove(BODY_CLASS);
  }, [isBar]);

  if (isBar) {
    return (
      <div
        role="status"
        data-testid="post-event-notice"
        className="fixed top-0 left-0 right-0 z-[60] flex items-center justify-center px-4 text-center h-16 md:h-11"
        style={{ backgroundColor: COLORS.charcoal }}
      >
        <p
          style={{
            ...typography.caption,
            color: COLORS.dustySky,
            fontSize: "11px",
            letterSpacing: "0.06em",
            lineHeight: 1.4,
          }}
        >
          {POST_EVENT.endedNotice}
        </p>
      </div>
    );
  }

  return (
    <div
      role="status"
      data-testid="post-event-notice-inline"
      className={`w-full border px-6 py-5 text-center ${className}`}
      style={{ backgroundColor: COLORS.charcoal, borderColor: `${COLORS.boulder}40` }}
    >
      <p
        style={{
          ...typography.subhead,
          color: COLORS.dustySky,
          fontSize: "16px",
          lineHeight: 1.5,
        }}
      >
        {POST_EVENT.endedNotice}
      </p>
    </div>
  );
}

export default PostEventNotice;
